/**
 * The map choice of an online room on the barracks screen (menu_just_play in room mode, design D5), in the original's
 * style: the offline screen lists its five "just" maps as "Карта №N" tasks (choose_task clips in just_play_container,
 * the chosen one red; new_interf init / render "just_play") and shows the chosen map's minimap in the screen's picture.
 * A room offers every PvP map (ONLINE_MAPS, src/session/online.ts), more than the list's masked area shows (12 rows),
 * so the list scrolls like the fight screen's task list: the same slider (menu_fight's `slider` button, dragged by
 * new_interf init_slider / slider_proceed, which know "just_play" for this).
 *  - The host (a room in the lobby) sees every map, the chosen one red, and picks one with a click (the lobby sends
 *    room.update); the list starts scrolled to the chosen map.
 *  - A guest (and the host while the battle runs) sees the room's map only: its entry at the top, read-only.
 * The entry of map N is named "N" (the click handler reads the map from the clicked entry, like the original's
 * choose_task_button handler). The list belongs to one screen instance: a new room screen gets a new list.
 *
 * Browser-safe: the Flash runtime and the session layer only.
 */
import { BitmapData, MouseEvent, Point, Rectangle } from '@flash';
import { ONLINE_MAPS, type OnlineMap } from '../../session/online';
import { listPitch } from '../ui/wide-lists';

/** just_play_container's y on menu_just_play (the top of the list). */
export const MAP_LIST_TOP = 182;
/**
 * Height of a row (new_interf init "just_play": entry n at y (n - 1) * 20); on a phone the rows are listPitch apart
 * (ui/wide-lists.ts), the list's mask the same.
 */
export const MAP_LIST_ROW = 20;
/** Rows the list's mask shows (menu_just_play's mask: y 182..433). */
export const MAP_LIST_ROWS = 12;
/**
 * The slider beside the list (to the right of its mask, as on the fight screen) and its track: new_interf.init_slider
 * uses the same top and bottom for "just_play".
 */
export const MAP_SLIDER = { x: 710, top: 191, bottom: 421 } as const;

/** Colours of the task names (new_interf render "just_play": the chosen map red, the others black). */
const CHOSEN = 16711680;
const OTHER = 0;

/** The minimap of a PvP map (314 x 115, its row of the family's strip), as new_interf.get_minimap_coords cuts it. */
export function onlineMinimap(interf: any, map: OnlineMap): BitmapData {
  const out = new BitmapData(314, 115, true, 0);
  out.copyPixels(interf.minimaps[map.family], new Rectangle(0, (map.number - 1) * 115, 314, 115), new Point(0, 0), null, null, true);
  return out;
}

export class RoomMapList {
  /** The screen instance the list is built on. */
  private link: any = null;
  private readonly entries = new Map<number, any>();
  private slider: any = null;
  /** The layout of the last render: every map (the host) or the room's map only. */
  private all: boolean | null = null;

  constructor(private readonly fwp: any) {}

  /** The entry clips by map number (tests, tools). */
  entry(mapIndex: number): any | null {
    return this.entries.get(mapIndex) ?? null;
  }

  /** Map numbers whose entries are shown. */
  shown(): number[] {
    return [...this.entries.entries()].filter(([, e]) => e.visible).map(([n]) => n);
  }

  /** Shows the room's map `chosen` on the screen `link`: every map for the host (`editable`), else the chosen one. */
  render(link: any, chosen: OnlineMap, editable: boolean): void {
    const i = this.fwp._interf;
    if (link !== this.link) this.build(link);
    const pitch = this.pitch();
    for (const [n, e] of this.entries) {
      e.task_name.textColor = n === chosen.mapIndex ? CHOSEN : OTHER;
      e.visible = editable || n === chosen.mapIndex;
      e.y = editable ? (n - 1) * pitch : 0;
    }
    this.slider.visible = editable && ONLINE_MAPS.length * pitch > MAP_LIST_ROWS * MAP_LIST_ROW;
    if (this.all !== editable) {
      this.all = editable;
      this.scrollTo(editable ? chosen.mapIndex : 1);
    } else if (editable && !this.inView(chosen.mapIndex)) this.scrollTo(chosen.mapIndex);
    const mini = onlineMinimap(i, chosen);
    i.MainBitmapData.copyPixels(mini, new Rectangle(0, 0, 342, 154), new Point(10, 10));
    mini.dispose();
  }

  /** The entries of every PvP map and the slider on a new room screen (the offline screen's five entries are hidden). */
  private build(link: any): void {
    const f = this.fwp;
    const i = f._interf;
    this.link = link;
    this.entries.clear();
    this.all = null;
    const box = link.just_play_container;
    for (let k = 0; k < box.numChildren; k++) box.getChildAt(k).visible = false;
    for (const m of ONLINE_MAPS) {
      const e = box.addChild(f.container_interf.get_object('choose_task'));
      e.x = 0;
      e.y = (m.mapIndex - 1) * this.pitch();
      e.task_name.htmlText = 'Карта №' + m.mapIndex;
      e.name = String(m.mapIndex);
      this.entries.set(m.mapIndex, e);
    }
    // The fight screen's slider button, moved onto this screen with the listeners every control of a screen has
    // (new_interf init): its mouseDown / mouseUp start and stop the drag (new_interf.mouse_event, slider_proceed).
    const fight = f.container_interf.get_object('menu_fight');
    const slider = fight.slider;
    fight.removeChild(slider);
    slider.x = MAP_SLIDER.x;
    slider.y = MAP_SLIDER.top;
    link.addChild(slider);
    link.slider = slider;
    slider.addEventListener(MouseEvent.CLICK, i.click, false, 0, true);
    for (const type of [MouseEvent.MOUSE_OVER, MouseEvent.MOUSE_OUT, MouseEvent.MOUSE_DOWN, MouseEvent.MOUSE_UP]) slider.addEventListener(type, i.mouse_event, false, 0, true);
    this.slider = slider;
  }

  /** The step between two rows (20 px; a phone's: ui/wide-lists.ts). */
  private pitch(): number {
    return listPitch(this.fwp.stage);
  }

  /** Scroll range of the list in px (new_interf.slider_proceed's formula for the fight screen's list). */
  private range(): number {
    return Math.max(0, this.link.just_play_container.height - MAP_LIST_ROW - (MAP_SLIDER.bottom - MAP_SLIDER.top));
  }

  /** The row of map `n` is inside the list's visible area. */
  private inView(n: number): boolean {
    const offset = MAP_LIST_TOP - this.link.just_play_container.y;
    const top = (n - 1) * this.pitch();
    return top >= offset && top + MAP_LIST_ROW <= offset + MAP_LIST_ROWS * MAP_LIST_ROW;
  }

  /** Scrolls the list (and puts the slider where a drag would have) so that the row of map `n` is in the middle. */
  private scrollTo(n: number): void {
    const range = this.range();
    const offset = Math.max(0, Math.min(range, (n - 1) * this.pitch() - ((MAP_LIST_ROWS - 1) * MAP_LIST_ROW) / 2));
    const p = range > 0 ? offset / range : 0;
    this.link.just_play_container.y = MAP_LIST_TOP - p * range;
    this.slider.y = MAP_SLIDER.top + p * (MAP_SLIDER.bottom - MAP_SLIDER.top);
  }
}
